import React, { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";
import {
  FaPercentage,
  FaPlus,
  FaTag,
  FaCalendarAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaTrashAlt,
  FaToggleOn,
  FaToggleOff,
} from "react-icons/fa";

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    code: "",
    discount_type: "P",
    amount: "",
    min_order_amount: "",
    max_discount: "",
    expiry_date: "",
  });

  const fetchCoupons = () => {
    setLoading(true);
    axios
      .get("/api/admin/coupons")
      .then((res) => {
        if (res.data.status === 200) {
          setCoupons(res.data.coupons || []);
        } else {
          // Fallback to active-coupons if needed
          axios.get("/api/active-coupons").then((r) => {
            setCoupons(r.data.coupons || []);
          });
        }
        setLoading(false);
      })
      .catch(() => {
        axios
          .get("/api/active-coupons")
          .then((r) => {
            setCoupons(r.data.coupons || []);
            setLoading(false);
          })
          .catch(() => {
            setLoading(false);
          });
      });
  };

  useEffect(() => {
    document.title = "Coupons & Promotions — Admin";
    fetchCoupons();
  }, []);

  const handleInput = (e) => {
    e.persist();
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveCoupon = (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      code: formData.code.toUpperCase().trim(),
      discount_type: formData.discount_type,
      amount: formData.amount,
      min_order_amount: formData.min_order_amount || 0,
      max_discount: formData.max_discount || null,
      expiry_date: formData.expiry_date || null,
    };

    axios
      .post("/api/admin/coupons", payload)
      .then((res) => {
        setSaving(false);
        if (res.data.status === 200) {
          swal("Success", "Coupon promotion created successfully", "success", {
            buttons: false,
            timer: 1200,
          });
          setOpenModal(false);
          setFormData({
            code: "",
            discount_type: "P",
            amount: "",
            min_order_amount: "",
            max_discount: "",
            expiry_date: "",
          });
          fetchCoupons();
        } else {
          const errMsg = res.data.errors
            ? Object.values(res.data.errors).flat().join("\n")
            : res.data.message || "Failed to create coupon.";
          swal("Error", errMsg, "error");
        }
      })
      .catch((err) => {
        setSaving(false);
        const errMsg =
          err.response?.data?.message ||
          (err.response?.data?.errors
            ? Object.values(err.response.data.errors).flat().join("\n")
            : "An unexpected error occurred while saving the coupon.");
        swal("Error", errMsg, "error");
      });
  };

  const handleToggleStatus = (id) => {
    axios
      .put(`/api/admin/coupons/${id}/toggle`)
      .then((res) => {
        if (res.data.status === 200) {
          swal("Status Updated", res.data.message, "success", {
            buttons: false,
            timer: 1000,
          });
          fetchCoupons();
        } else {
          swal("Error", res.data.message || "Could not toggle status", "error");
        }
      })
      .catch(() => {
        swal("Error", "Could not toggle status on server.", "error");
      });
  };

  const handleDeleteCoupon = (id, code) => {
    swal({
      title: "Delete Coupon?",
      text: `Are you sure you want to permanently delete coupon "${code}"?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        axios
          .delete(`/api/admin/coupons/${id}`)
          .then((res) => {
            if (res.data.status === 200) {
              swal("Deleted", "Coupon removed successfully.", "success", {
                buttons: false,
                timer: 1000,
              });
              fetchCoupons();
            } else {
              swal("Error", res.data.message || "Could not delete coupon.", "error");
            }
          })
          .catch(() => {
            swal("Error", "Could not delete coupon on server.", "error");
          });
      }
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: "var(--admin-text-main)" }}>
            Coupons & Promo Codes
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            Create and manage promotional discount coupons for checkout savings
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpenModal(true)}
          className="btn btn-primary rounded-pill px-4 py-2 fw-semibold d-flex align-items-center gap-2"
          style={{ fontSize: "13.5px", background: "var(--admin-primary)", border: "none" }}
        >
          <FaPlus size={12} /> Create Coupon
        </button>
      </div>

      {/* Main Table Card */}
      <div className="admin-card">
        <div className="table-responsive">
          {loading ? (
            <div className="p-5 text-center">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : coupons.length === 0 ? (
            <div className="p-5 text-center">
              <FaPercentage size={36} className="text-muted mb-2" />
              <h6 className="fw-bold">No Active Coupons Found</h6>
              <p className="text-muted mb-3" style={{ fontSize: "13px" }}>
                Create your first promotional discount coupon code for your customers.
              </p>
              <button
                type="button"
                className="btn btn-sm btn-dark rounded-pill px-3"
                onClick={() => setOpenModal(true)}
              >
                Create Promo Code
              </button>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Promo Code</th>
                  <th>Discount Type</th>
                  <th>Savings Value</th>
                  <th>Min Order Spend</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => {
                  const isPercent = c.discount_type === "P" || c.type === "percent";
                  const isActive = c.status === "active" || c.status === 1 || c.status === "1";
                  const savingsDisplay = isPercent
                    ? `${c.amount || c.value}% OFF`
                    : `Rs. ${Number(c.amount || c.value).toLocaleString()} OFF`;
                  const minSpend = Number(c.min_order_amount || 0);

                  return (
                    <tr key={c.id || c.code}>
                      <td>
                        <code className="fw-bold fs-6 text-primary px-2 py-1 bg-light rounded border">
                          {c.code}
                        </code>
                      </td>
                      <td>
                        <span className="badge bg-light text-dark border">
                          {isPercent ? "Percentage Discount" : "Fixed PKR Amount"}
                        </span>
                      </td>
                      <td>
                        <span className="fw-bold text-success">{savingsDisplay}</span>
                        {isPercent && c.max_discount ? (
                          <div className="text-muted" style={{ fontSize: "11px" }}>
                            Max: Rs. {Number(c.max_discount).toLocaleString()}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {minSpend > 0 ? (
                          <span className="badge bg-light text-secondary border">
                            Rs. {minSpend.toLocaleString()}
                          </span>
                        ) : (
                          <span className="text-muted" style={{ fontSize: "12px" }}>
                            None
                          </span>
                        )}
                      </td>
                      <td>
                        <span className="text-muted d-flex align-items-center gap-1">
                          <FaCalendarAlt size={11} />
                          {c.expiry_date || c.expires_at
                            ? new Date(c.expiry_date || c.expires_at).toLocaleDateString("en-PK", {
                                dateStyle: "medium",
                              })
                            : "No Expiry"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            isActive ? "bg-success" : "bg-secondary"
                          } px-2 py-1`}
                          style={{ fontSize: "11px" }}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          {c.id && (
                            <>
                              <button
                                type="button"
                                className={`btn btn-sm ${
                                  isActive ? "btn-outline-warning" : "btn-outline-success"
                                } py-1 px-2`}
                                title={isActive ? "Deactivate Coupon" : "Activate Coupon"}
                                onClick={() => handleToggleStatus(c.id)}
                              >
                                {isActive ? (
                                  <FaToggleOn size={14} />
                                ) : (
                                  <FaToggleOff size={14} />
                                )}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger py-1 px-2"
                                title="Delete Coupon"
                                onClick={() => handleDeleteCoupon(c.id, c.code)}
                              >
                                <FaTrashAlt size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Coupon Modal */}
      {openModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1050 }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold" style={{ fontSize: "16px" }}>
                  Create Promotional Coupon
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setOpenModal(false)}
                ></button>
              </div>
              <form onSubmit={handleSaveCoupon}>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Coupon Promo Code
                    </label>
                    <input
                      type="text"
                      name="code"
                      required
                      placeholder="e.g. WELCOME10, SAVE500"
                      className="form-control text-uppercase fw-bold"
                      value={formData.code}
                      onChange={handleInput}
                      style={{ letterSpacing: "1px" }}
                    />
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                        Discount Type
                      </label>
                      <select
                        name="discount_type"
                        className="form-select"
                        value={formData.discount_type}
                        onChange={handleInput}
                      >
                        <option value="P">Percentage (%)</option>
                        <option value="F">Fixed Amount (PKR)</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                        Discount Value
                      </label>
                      <input
                        type="number"
                        name="amount"
                        required
                        min="1"
                        placeholder={
                          formData.discount_type === "P" ? "e.g. 15 (%)" : "e.g. 200 (PKR)"
                        }
                        className="form-control fw-bold"
                        value={formData.amount}
                        onChange={handleInput}
                      />
                    </div>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                        Min Order Spend (PKR)
                      </label>
                      <input
                        type="number"
                        name="min_order_amount"
                        min="0"
                        placeholder="e.g. 1000"
                        className="form-control"
                        value={formData.min_order_amount}
                        onChange={handleInput}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                        Max Discount Cap (Optional)
                      </label>
                      <input
                        type="number"
                        name="max_discount"
                        min="1"
                        placeholder="e.g. 1500"
                        className="form-control"
                        value={formData.max_discount}
                        onChange={handleInput}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold" style={{ fontSize: "13px" }}>
                      Expiration Date (Optional)
                    </label>
                    <input
                      type="date"
                      name="expiry_date"
                      className="form-control"
                      value={formData.expiry_date}
                      onChange={handleInput}
                    />
                  </div>
                </div>
                <div className="modal-footer border-top bg-light">
                  <button
                    type="button"
                    className="btn btn-sm btn-light border"
                    onClick={() => setOpenModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-sm btn-primary fw-semibold px-3"
                  >
                    {saving ? "Saving..." : "Create Coupon"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
